import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import InputSettingsField from './InputSettingsField';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import Button from '../../misc/Button';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import CheckboxSettingsField from './CheckboxSettingsField';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import RequiredLogicSettings from './RequiredLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import EditabilitySettings from './EditabilitySettings';
import TitleSettings from './TitleSettings';
import DecoratorSettings from './DecoratorSettings';
import RowSpecificDecoratorSettings from './RowSpecificDecoratorSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine, StyledButtonGroupWrapper } from './SettingsCommonElements';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';
import FieldTypes from '../../../constants/FieldTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsTableField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,
  requiredLogic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldAliasId,
  fieldSection,

  title,
  formula,
  type,
  displayType,
  x,
  y,
  width,
  height,
  identifier,
  metadata,
  config,
  required,
  hidden,
  editability,
  hiddenViews,
  disabled,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Column Headers styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,
  columnTitleTextOverflow,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  decorators,
  onDecoratorCreate,
  onDecoratorChange,
  onDecoratorDelete,

  rowDecorators,
  onRowDecoratorCreate,
  onRowDecoratorChange,
  onRowDecoratorDelete,

  value,
  rowOrder,
  onRowSpecificDecoratorCreate,
  onRowSpecificDecoratorChange,
  onRowSpecificDecoratorDelete,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,
  tipTitle,
  tipDescription,
  tipVisible,
  useReadableIds,

}) => {

  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');
  const isPricingTable = type === FieldTypes.pricingTable;

  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisplayTypeHidden = isPricingTable ? true : isSettingHidden(config, 'displayType');
  const isTitleHidden = isSettingHidden(config, 'title');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isIdHidden = isSettingHidden(config, '_id');
  const isDecoratorsHidden = isSettingHidden(config, 'decorators');
  const isRowDecoratorsHidden = isSettingHidden(config, 'rowDecorators');
  const isRowSpecificDecoratorsHidden = isSettingHidden(config, 'rowSpecificDecorators');

  /**
   * editability lives on a table field. A collection sets it per schema instead (see
   * FieldSettingsCollectionSchema), and the compact view (rte) is outside the feature.
   */
  const isEditabilityHidden = isSettingHidden(config, 'editability')
    || type !== FieldTypes.table
    || displayType === FieldDisplayTypes.rte;

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Column Styles
  const isColumnTitleFontSizeHidden = isStyleSettingHidden(config, 'columnTitleFontSize');
  const isColumnTitleFontColorHidden = isStyleSettingHidden(config, 'columnTitleFontColor');
  const isColumnTitleTextAlignHidden = isStyleSettingHidden(config, 'columnTitleTextAlign');
  const isColumnTitleFontStyleHidden = isStyleSettingHidden(config, 'columnTitleFontStyle');
  const isColumnTitleFontWeightHidden = isStyleSettingHidden(config, 'columnTitleFontWeight');
  const isColumnTitleTextTransformHidden = isStyleSettingHidden(config, 'columnTitleTextTransform');
  const isColumnTitleTextDecorationHidden = isStyleSettingHidden(config, 'columnTitleTextDecoration');
  const isColumnTitleBackgroundColorHidden = isStyleSettingHidden(config, 'columnTitleBackgroundColor');
  const isColumnTitlePaddingHidden = isStyleSettingHidden(config, 'columnTitlePadding');
  const isColumnTitleTextOverflowHidden = isStyleSettingHidden(config, 'columnTitleTextOverflow');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isCellSettingsHidden = isPricingTable ? true : isFontSizeHidden
    && isFontColorHidden
    && isTextAlignHidden
    && isFontStyleHidden
    && isFontWeightHidden
    && isTextTransformHidden
    && isTextDecorationHidden
    && isTextOverflowHidden
    && isBackgroundColorHidden
    && isPaddingHidden;

  const nextFieldSettingsIcon = displayType === FieldDisplayTypes.inputGroup
    ? ThemeIcons.fields.tableInputGroup
    : displayType === FieldDisplayTypes.rte
      ? ThemeIcons.fields.richText
      : ThemeIcons.fields.table;

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);


  const metadataOptions = getMetadataOptions(config);

  const stylesNode = (
    <>
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Title"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(isColumnTitleFontSizeHidden
        && isColumnTitleFontColorHidden
        && isColumnTitleTextAlignHidden
        && isColumnTitleFontStyleHidden
        && isColumnTitleFontWeightHidden
        && isColumnTitleTextTransformHidden
        && isColumnTitleBackgroundColorHidden
        && isColumnTitlePaddingHidden
        && isColumnTitleTextDecorationHidden
        && isColumnTitleTextOverflowHidden) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Column"
            height={270}
          >
            <FieldStyleSettings
              id={`${fieldId}-column`}
              {...{

                fontSize: columnTitleFontSize,
                onFontSizeChange: isColumnTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleFontSize: value }),

                fontColor: columnTitleFontColor,
                onFontColorChange: isColumnTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleFontColor: value }),

                textAlign: columnTitleTextAlign,
                onTextAlignChange: isColumnTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleTextAlign: value }),

                fontStyle: columnTitleFontStyle,
                onFontStyleChange: isColumnTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleFontStyle: value }),

                fontWeight: columnTitleFontWeight,
                onFontWeightChange: isColumnTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleFontWeight: value }),

                textTransform: columnTitleTextTransform,
                onTextTransformChange: isColumnTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleTextTransform: value }),

                textDecoration: columnTitleTextDecoration,
                onTextDecorationChange: isColumnTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleTextDecoration: value }),

                backgroundColor: columnTitleBackgroundColor,
                onBackgroundColorChange: isColumnTitleBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleBackgroundColor: value }),

                padding: columnTitlePadding,
                onPaddingChange: isColumnTitlePaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitlePadding: value }),

                textOverflow: columnTitleTextOverflow,
                onTextOverflowChange: isColumnTitleTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { columnTitleTextOverflow: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isCellSettingsHidden ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Cell"
            height={270}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

                backgroundColor,
                onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

                padding,
                onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isMarginHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={320}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid:  Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );

  const settingsNode = (
    <>
      {isDisplayTypeHidden ? null : (
        <StyledButtonGroupWrapper>
          <ButtonGroupSettings
            id="displayType"
            types={[
              { type: FieldDisplayTypes.original, icon: ThemeIcons.fields.table, active: displayType === FieldDisplayTypes.original },
              { type: FieldDisplayTypes.inputGroup, icon: ThemeIcons.fields.tableInputGroup, active: displayType === FieldDisplayTypes.inputGroup },
              { type: FieldDisplayTypes.rte, icon: ThemeIcons.fields.richText, active: displayType === FieldDisplayTypes.rte },
            ]}
            onChange={(value) => {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { displayType: value });
            }}
          />
        </StyledButtonGroupWrapper>
      )}
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {(isRequiredHidden
        && isDisabledHidden
      ) ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isEditabilityHidden ? null : (
        <EditabilitySettings
          id="field"
          editability={editability}
          onChange={(next) => onChange(fieldId, fieldIdentifier, fieldPositionId, { editability: next })}
        />
      )}
      {isRequiredHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
            />
          </StyledFieldWrapper>
        )
      )}
      {isHeaderOrFooter || fields.length < 2 ? null : (
        <>
          <SettingsLabel>Required Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <RequiredLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={requiredLogic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { requiredLogic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Conditional Logic</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            {isLogicHidden || fields.length < 2 ? null : (
              <StyledFieldWrapper>
                <ConditionalLogicSettings
                  fileId={fileId}
                  targetLabel=" field"
                  targetFieldId={fieldId}
                  sortedPages={sortedPages}
                  fields={fields}
                  logic={logic}
                  onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
                />
              </StyledFieldWrapper>
            )}
            {isHiddenHidden ? null : (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.hide}
                  iconColor={hidden ? theme.warningColor : theme.borderColor}
                  id="hidden"
                  label="Hidden"
                  selected={hidden}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
                />
              </StyledFieldWrapper>
            )}
          </>
        )
      )}
      {isVisibilityHidden ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Visibility</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsDesktop"
                label="Hide on desktop"
                selected={hiddenViews?.includes('desktop') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'desktop']
                    : (hiddenViews || []).filter(v => v !== 'desktop');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsMobile"
                label="Hide on mobile"
                selected={hiddenViews?.includes('mobile') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'mobile']
                    : (hiddenViews || []).filter(v => v !== 'mobile');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsPdf"
                label="Hide on pdf"
                selected={hiddenViews?.includes('pdf') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'pdf']
                    : (hiddenViews || []).filter(v => v !== 'pdf');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {!metadataOptions || isPricingTable ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={decorators}
            onCreate={(data) => onDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            onChange={(decoratorAction, data) => onDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorAction, data)}
            onDelete={(decoratorAction) => onDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorAction)}
          />
        </>
      )}
      {isRowDecoratorsHidden || type === FieldTypes.collection ? null : (
        <>
          <SettingsLabel>Common Row Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={rowDecorators}
            onCreate={(data) => onRowDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            onChange={(decoratorAction, data) => onRowDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorAction, data)}
            onDelete={(decoratorAction) => onRowDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorAction)}
          />
        </>
      )}
      {isRowSpecificDecoratorsHidden || type === FieldTypes.collection ? null : (
        <>
          <SettingsLabel>Specific Row Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <RowSpecificDecoratorSettings
            rows={value}
            rowOrder={rowOrder}
            rowCommonDecorators={rowDecorators}
            onCreate={(rowId, data) => onRowSpecificDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, rowId, data)}
            onChange={(rowId, decoratorAction, data) => onRowSpecificDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, rowId, decoratorAction, data)}
            onDelete={(rowId, decoratorAction) => onRowSpecificDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, rowId, decoratorAction)}
          />
        </>
      )}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Tooltip"
            height={166}
          >
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />
            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsTableField;
